import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { buildBasemapStyle } from './basemap';
import type { MapModel, MapPin, MapTrack, RouteLeg } from './model';
import { renderMarkdown } from './markdown';
import { drawnRoute, isRealTrack, journeyPins } from './extract';
import { isMapUseKey, MapGestures, type Gestures } from './gestures';
import { framePaddingBottom, PanControl } from './pan';
import { hintMoreBelow } from './scroll-hint';
import type { DetailsSheet } from './sheet';

let protocolRegistered = false;

const MODE_COLOR: Record<string, string> = {
  train: '#0a6156',
  rail: '#0a6156',
  subway: '#0a3e37',
  tram: '#1f7f72',
  lightrail: '#1f7f72',
  bus: '#3f8e3a',
  ferry: '#2d6f9e',
  bike: '#93c01c',
  car: '#5a6b66',
  scooter: '#c2185b',
  walk: '#6b7a76',
  transit: '#0a6156',
};

/** Distinct colours for several tracks in one answer (six Heide-Shuttle rings). */
const TRACK_PALETTE = ['#0a6156', '#c2185b', '#1565c0', '#ef6c00', '#6a1b9a', '#2e7d32', '#00838f', '#8d6e63'];

/** MapLibre's own words — control labels a screen reader reads out, the gesture hints. */
const GERMAN_UI = {
  'AttributionControl.ToggleAttribution': 'Quellenangaben ein- oder ausblenden',
  // the pane around the map is the "Karte" landmark already
  'Map.Title': 'Kartenansicht',
  'NavigationControl.ZoomIn': 'Vergrößern',
  'NavigationControl.ZoomOut': 'Verkleinern',
  'Popup.Close': 'Schließen',
  'CooperativeGesturesHandler.WindowsHelpText': 'Zum Zoomen Strg gedrückt halten und scrollen',
  'CooperativeGesturesHandler.MacHelpText': 'Zum Zoomen ⌘ gedrückt halten und scrollen',
  // shown when one finger tries to drag the map in a page: the tap is the single-finger way (MapGestures)
  'CooperativeGesturesHandler.MobileHelpText': 'Zum Verschieben auf die Karte tippen oder mit zwei Fingern ziehen',
};

interface PillSpec { label: string; title: string; colour: string; coords: [number, number][]; primary: boolean }

export class AnswerMap {
  private map: maplibregl.Map;
  private markers: maplibregl.Marker[] = [];
  private ready: Promise<void>;
  private pending: MapModel | null = null;
  private layersReady = false;
  private readonly gestures: MapGestures;
  /** the attribution was folded since the map last became narrower than ATTRIBUTION_FOLD_WIDTH */
  private attributionFolded = false;
  /** folds the attribution of a narrow map once it has been in sight its time */
  private attributionTimer: ReturnType<typeof setTimeout> | undefined;
  /** at least half of the map lies in the window: only then does the attribution's time run */
  private inSight = false;

  /** `sheet`: where a place opens when the map is too low for a popup */
  constructor(container: HTMLElement, tilesUrl: string, home: { lat: number; lon: number; zoom: number }, private readonly sheet: DetailsSheet) {
    if (!protocolRegistered) {
      maplibregl.addProtocol('pmtiles', new Protocol().tile);
      protocolRegistered = true;
    }
    this.map = new maplibregl.Map({
      container,
      style: buildBasemapStyle(tilesUrl),
      center: [home.lon, home.lat],
      zoom: home.zoom,
      attributionControl: false,
      // until the widget says otherwise (setGestures): one finger and the plain wheel scroll the page
      // around the map, two fingers move it, Ctrl/⌘ + wheel zooms (and the map says so)
      cooperativeGestures: true,
      locale: GERMAN_UI,
    });
    this.gestures = new MapGestures(this.map);
    this.map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.addControl(new PanControl(), 'bottom-left');
    // the attribution opens once a source names its credits (sourcedata); a resize can make the map narrow
    for (const event of ['resize', 'styledata', 'sourcedata'] as const) this.map.on(event, this.foldAttributionLater);
    // a visitor working the map has seen the credit: a click or tap on it (a pin, a zoom button), its
    // keys, or a move of their own — not a wheel or swipe that only scrolls the page around it
    for (const event of ['click', 'keydown'] as const) container.addEventListener(event, this.onMapUse, { capture: true });
    this.map.on('movestart', (e) => {
      if (e.originalEvent) this.foldAttributionNow();
    });
    // a map below the page's fold (or scrolled away) has not been seen: its five seconds wait
    new IntersectionObserver((entries) => {
      // one call may carry several changes of the map (laid out: 0, then all of it); the last counts
      const entry = entries[entries.length - 1];
      this.inSight = !!entry && entry.intersectionRatio >= ATTRIBUTION_IN_SIGHT_SHARE;
      if (this.inSight) this.foldAttributionLater();
      else this.stopAttributionTimer();
    }, { threshold: ATTRIBUTION_IN_SIGHT_SHARE }).observe(container);
    // `style.load` (style parsed, sources known) — NOT `load`, which also waits for the first
    // tiles; with a slow tile server the answer would sit in `pending` until they arrive
    this.ready = new Promise((resolve) => (this.map.isStyleLoaded() ? resolve() : this.map.once('style.load', () => resolve())));
    void this.ready.then(() => {
      this.map.addSource('legs', { type: 'geojson', data: empty() });
      const primary = (on: number, off: number): maplibregl.ExpressionSpecification => ['case', ['get', 'primary'], on, off];
      // The trip the answer tells is the picture; an alternative the text names is a faint hint behind it.
      this.map.addLayer({
        id: 'legs-casing', type: 'line', source: 'legs', filter: ['get', 'primary'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 9, 'line-opacity': 0.95 },
      });
      // A real track (pathSource geo) is drawn solid…
      this.map.addLayer({
        id: 'legs-real', type: 'line', source: 'legs', filter: ['all', ['!=', ['get', 'mode'], 'walk'], ['==', ['get', 'schematic'], false]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': primary(5, 2), 'line-opacity': primary(1, 0.35) },
      });
      // …the router's stop-to-stop chain is drawn dashed, so nobody reads it as the track.
      this.map.addLayer({
        id: 'legs-schematic', type: 'line', source: 'legs', filter: ['all', ['!=', ['get', 'mode'], 'walk'], ['==', ['get', 'schematic'], true]],
        layout: { 'line-cap': 'butt', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': primary(4, 2), 'line-opacity': primary(0.9, 0.35), 'line-dasharray': [2.2, 1.4] },
      });
      this.map.addLayer({
        id: 'legs-walk', type: 'line', source: 'legs', filter: ['==', ['get', 'mode'], 'walk'],
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': primary(3, 2), 'line-dasharray': [0.8, 1.6], 'line-opacity': primary(0.9, 0.3) },
      });
      // tracks (a tour's route, a ring line): solid, one colour per track
      this.map.addSource('tracks', { type: 'geojson', data: empty() });
      this.map.addLayer({
        id: 'tracks-casing', type: 'line', source: 'tracks',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.9 },
      });
      this.map.addLayer({
        id: 'tracks', type: 'line', source: 'tracks',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.95 },
      });
      // intermediate halts of the trip's rides: small dots, no pins, no popups
      this.map.addSource('halts', { type: 'geojson', data: empty() });
      this.map.addLayer({
        id: 'halts', type: 'circle', source: 'halts',
        paint: { 'circle-radius': 3.2, 'circle-color': '#ffffff', 'circle-stroke-color': ['get', 'color'], 'circle-stroke-width': 1.6 },
      });
      this.layersReady = true;
      if (this.pending) this.show(this.pending);
    });
  }

  resize(): void {
    this.map.resize();
  }

  setGestures(mode: Gestures): void {
    this.gestures.set(mode);
  }

  /** The widget leaves the page: a finger lent to the map goes back, with the listeners on the page. */
  returnFinger(): void {
    this.gestures.giveBack();
  }

  /** MapLibre opens its compact attribution at first; on a narrow map the open bar covers a good part
   *  of the bottom edge, so it is folded there (the ⓘ button opens it again). The OSMF attribution
   *  guideline lets a credit collapse on a map interaction or after five seconds — so it stands open
   *  that long first, counted from when it shows (`ATTRIBUTION_SHOWN_MS`) with at least half of the
   *  map in the window — a map that leaves the window before starts its five seconds anew — or until
   *  the visitor works the map. It folds once each time the map becomes narrow, and never after the
   *  visitor used the ⓘ button: who opens it on a narrow map keeps it open. */
  private readonly foldAttributionLater = (): void => {
    const width = this.map.getContainer().clientWidth;
    // a hidden map has no width; it is measured when it comes into sight
    if (width === 0) return;
    if (width >= ATTRIBUTION_FOLD_WIDTH) {
      this.attributionFolded = false;
      this.stopAttributionTimer();
      return;
    }
    // not open yet: MapLibre opens it once a source has named its credits, and the next `sourcedata` comes back here
    if (this.attributionFolded || this.attributionTimer !== undefined || !this.inSight || !this.openAttribution()) return;
    this.attributionTimer = setTimeout(() => {
      this.attributionTimer = undefined;
      this.foldAttributionNow();
    }, ATTRIBUTION_SHOWN_MS);
  };

  private readonly onMapUse = (e: Event): void => {
    // Tab through the map's pins and buttons only passes by
    if (e instanceof KeyboardEvent && !isMapUseKey(e.key)) return;
    if (!(e.target as Element).closest('.maplibregl-ctrl-attrib-button')) {
      this.foldAttributionNow();
      return;
    }
    // the visitor opens and closes the credit themselves (its ⓘ): from now on it stays as they leave it
    this.stopAttributionTimer();
    this.attributionFolded = true;
  };

  /** Fold the open attribution of a narrow map, once. MapLibre's own toggle keeps the class and the
   *  `<details>` state together; so does this, or the button would say "expanded". */
  private foldAttributionNow(): void {
    this.stopAttributionTimer();
    const width = this.map.getContainer().clientWidth;
    if (this.attributionFolded || width === 0 || width >= ATTRIBUTION_FOLD_WIDTH) return;
    const open = this.openAttribution();
    if (!open) return;
    open.classList.remove('maplibregl-compact-show');
    open.removeAttribute('open');
    this.attributionFolded = true;
  }

  private openAttribution(): Element | null {
    return this.map.getContainer().querySelector('.maplibregl-ctrl-attrib.maplibregl-compact-show');
  }

  private stopAttributionTimer(): void {
    clearTimeout(this.attributionTimer);
    this.attributionTimer = undefined;
  }

  show(model: MapModel): void {
    // Gate on our own flag, not on `isStyleLoaded()`: that one flips to false
    // whenever a source is still fetching tiles, and a model parked as `pending`
    // at such a moment was never drawn (the flush only runs once, on `load`).
    if (!this.layersReady) {
      this.pending = model;
      return;
    }
    this.pending = null;
    // a place of the answer that goes from the map
    this.sheet.close(false);
    for (const m of this.markers) m.remove();
    this.markers = [];

    // The trip the answer tells — out of the recommended journey and its continuation segments (the
    // onward bike/bus ride planned as a second connections call), each section from its boarding to its
    // alighting — and, faint, the alternatives the text names (extract.ts::routeOf). Never the router's
    // other journeys or those of a query the answer did not use: an unlabelled line the text does not
    // speak of raises more questions than it answers (Stakeholder 2026-09-08, 2026-10-28).
    const features: GeoJSON.Feature[] = [];
    const drawn: { leg: RouteLeg; coords: [number, number][] }[] = [];
    for (const leg of drawnRoute(model)) {
      // Solid = the real track (`isRealTrack`: geo, or papi's own course on the network), dashed = a
      // chain of halts that only looks like a path.
      const real = isRealTrack(leg);
      const coords: [number, number][] = leg.path.length >= 2 ? leg.path : leg.nodes.filter((n) => n.lat !== undefined && n.lon !== undefined).map((n) => [n.lon!, n.lat!]);
      if (coords.length < 2) continue;
      const mode = modeKey(leg.mode);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { line: leg.line, mode, color: leg.colour ?? MODE_COLOR[mode] ?? '#0a6156', schematic: !real, primary: !leg.alternative },
      });
      drawn.push({ leg, coords });
    }
    // the trip on top of the alternatives
    features.sort((a, b) => Number(a.properties!.primary) - Number(b.properties!.primary));
    (this.map.getSource('legs') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features });

    for (const pin of model.pins) this.markers.push(this.marker(pin));

    // Pins and labels follow the answer's own priority: the trip gets its boarding, transfer and
    // alighting stops as pins (one per station, extract.ts::journeyPins), its intermediate halts as
    // dots and its line badges; alternatives stay faint lines without any of that.
    for (const pin of journeyPins(model)) this.markers.push(this.marker(pin));
    const halts: GeoJSON.Feature[] = [];
    for (const { leg } of drawn) {
      if (leg.alternative || modeKey(leg.mode) === 'walk') continue;
      const color = leg.colour ?? MODE_COLOR[modeKey(leg.mode)] ?? '#0a6156';
      const nodes = leg.nodes.filter((n) => n.lat !== undefined && n.lon !== undefined);
      for (const n of nodes.slice(1, -1)) halts.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [n.lon!, n.lat!] }, properties: { color } });
    }
    (this.map.getSource('halts') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: halts });

    const tracks = model.tracks ?? [];
    const trackFeatures: GeoJSON.Feature[] = tracks.map((t, i) => ({
      type: 'Feature', geometry: { type: 'LineString', coordinates: t.path }, properties: { color: trackColour(i), name: t.name },
    }));
    (this.map.getSource('tracks') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: trackFeatures });

    const pills: PillSpec[] = [
      ...drawn.filter((d) => !d.leg.alternative && modeKey(d.leg.mode) !== 'walk').map((d) => ({
        label: d.leg.line, title: [d.leg.line, d.leg.direction ? `→ ${d.leg.direction}` : '', d.leg.operator ?? ''].filter(Boolean).join(' '),
        colour: d.leg.colour ?? MODE_COLOR[modeKey(d.leg.mode)] ?? '#0a6156', coords: d.coords, primary: true,
      })),
      ...tracks.map((t, i) => ({ label: trackLabel(t), title: t.name, colour: trackColour(i), coords: t.path, primary: true })),
    ];
    this.placePills(pills);

    // the view frames the pins and the trip; an alternative via another town must not zoom the map out
    const focus = focusBounds(model);
    if (focus) {
      const [a, b, c, d] = focus;
      const single = Math.abs(c - a) < 1e-6 && Math.abs(d - b) < 1e-6;
      if (single) this.map.easeTo({ center: [a, b], zoom: 14, duration: 600 });
      // top/right leave room for the zoom control, bottom for the attribution and the pan buttons, whose
      // size (`--ctrl`, larger on touch) the stylesheet sets
      else {
        const ctrl = parseFloat(getComputedStyle(this.map.getContainer()).getPropertyValue('--ctrl'));
        this.map.fitBounds([[a, b], [c, d]], { padding: { top: 84, bottom: framePaddingBottom(ctrl), left: 56, right: 88 }, maxZoom: 15, duration: 700 });
      }
    }
  }

  /** One readable badge per line or track, at the middle of its course. Badges that land
   *  in the same place (a shared corridor, a 0.02° ≈ 1.5 km slot) stack instead of
   *  writing on top of each other. */
  private placePills(pills: PillSpec[]): void {
    const seen = new Map<string, number>(); // slot → how many badges already sit there
    const shown = new Set<string>();
    const ordered = [...pills].sort((a, b) => Number(a.primary) - Number(b.primary)); // primary last = on top
    for (const p of ordered) {
      if (p.coords.length < 2) continue;
      // direction-agnostic: the outbound and the return over the same corridor (Hin- und Rückweg,
      // a bike "Rad" pill each) share a badge — sort the two endpoints so A→B and B→A collapse.
      const ends = [p.coords[0]!.join(','), p.coords[p.coords.length - 1]!.join(',')].sort().join('~');
      const key = `${p.label}|${ends}`;
      if (shown.has(key)) continue; // the same line over the same corridor twice, either direction
      shown.add(key);
      const mid = midpoint(p.coords);
      const slot = `${(Math.round(mid[0] / 0.02) * 0.02).toFixed(2)},${(Math.round(mid[1] / 0.02) * 0.02).toFixed(2)}`;
      const n = seen.get(slot) ?? 0;
      seen.set(slot, n + 1);
      const el = document.createElement('div');
      el.className = `line-pill${p.primary ? ' is-primary' : ''}`;
      el.style.zIndex = p.primary ? '3' : '2';
      el.style.setProperty('--pill', p.colour);
      el.textContent = p.label;
      el.title = p.title;
      this.markers.push(new maplibregl.Marker({ element: el, anchor: 'center', offset: [0, -n * 24] }).setLngLat(mid).addTo(this.map));
      // the answer's text names the lines; a badge repeats it for the eye. MapLibre names every marker
      // „Map marker“, which a <div> without a role may not carry.
      el.removeAttribute('aria-label');
      el.setAttribute('aria-hidden', 'true');
    }
  }

  private marker(pin: MapPin): maplibregl.Marker {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `pin pin-${pin.kind}`;
    el.setAttribute('aria-label', pin.name);
    el.innerHTML = pin.kind === 'stop' ? ICON_STOP : ICON_DROP;
    const details = document.createElement('template');
    details.innerHTML = popupHtml(pin);
    const body = details.content.firstElementChild as HTMLElement;
    hintMoreBelow(body);
    const popup = new maplibregl.Popup({ offset: 18, closeButton: true, maxWidth: `${POPUP_MAX_WIDTH}px` }).setDOMContent(body);
    popup.on('open', () => {
      // a map this low holds no readable popup: the place opens as a sheet over the widget instead
      if (this.map.getContainer().clientHeight < SHEET_BELOW_MAP_HEIGHT) {
        popup.remove();
        this.sheet.open(popupHtml(pin), el);
        return;
      }
      this.fitPopup(popup, body);
      this.keepInView(popup);
    });
    const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([pin.lon, pin.lat]).setPopup(popup).addTo(this.map);
    // MapLibre stamps every marker "Map marker" — a screen reader should hear the place's name
    el.setAttribute('aria-label', pin.name);
    el.title = pin.name;
    return marker;
  }

  /** A popup never grows past the map, nor into the strip on the right where the zoom buttons sit
   *  (a popup over them would hide them). Its width is capped to the free part of the map, the body
   *  of our `.popup` to the live map height minus a margin; taller content scrolls inside the bubble.
   *  The MapLibre close button sits in the content wrapper (not `.popup`), so it stays put while the
   *  body scrolls. */
  private fitPopup(popup: maplibregl.Popup, body: HTMLElement): void {
    const free = this.freeArea();
    popup.setMaxWidth(`${Math.max(180, Math.min(POPUP_MAX_WIDTH, free.right - free.left - 2 * POPUP_MARGIN))}px`);
    // leave room above and below (margin) plus the content's own padding, so the bubble sits inside the map
    body.style.maxHeight = `${Math.max(140, this.map.getContainer().clientHeight - 56)}px`;
  }

  /** A popup that opens over the edge of the map (its footer link cut off) or over the zoom buttons
   *  nudges the map so it fits. */
  private keepInView(popup: maplibregl.Popup): void {
    requestAnimationFrame(() => {
      const el = popup.getElement();
      if (!el) return;
      const r = el.getBoundingClientRect();
      const c = this.freeArea();
      const m = POPUP_MARGIN;
      let dx = 0, dy = 0;
      if (r.bottom > c.bottom - m) dy = r.bottom - (c.bottom - m);
      else if (r.top < c.top + m) dy = r.top - (c.top + m);
      if (r.right > c.right - m) dx = r.right - (c.right - m);
      else if (r.left < c.left + m) dx = r.left - (c.left + m);
      if (dx || dy) this.map.panBy([dx, dy], { duration: 250 });
    });
  }

  /** The map's box without the strip of the controls in its top-right corner. */
  private freeArea(): { left: number; top: number; right: number; bottom: number } {
    const container = this.map.getContainer();
    const c = container.getBoundingClientRect();
    const controls = container.querySelector('.maplibregl-ctrl-top-right')?.getBoundingClientRect().width ?? 0;
    return { left: c.left, top: c.top, right: c.right - controls, bottom: c.bottom };
  }
}

const POPUP_MAX_WIDTH = 280;
/** below this map height (px) a place opens as a sheet over the widget: a popup would show a few lines
 *  under its photo (a 255 px map leaves the popup's body 200 px) */
const SHEET_BELOW_MAP_HEIGHT = 300;
/** below this map width (px) the attribution folds after it has been in sight */
const ATTRIBUTION_FOLD_WIDTH = 400;
/** how long the attribution of a narrow map stands open (OSMF guideline: „automatically after five seconds“) */
const ATTRIBUTION_SHOWN_MS = 5000;
/** the share of the map that has to lie in the window for those seconds to count */
const ATTRIBUTION_IN_SIGHT_SHARE = 0.5;
/** the distance a popup keeps from the map's edges */
const POPUP_MARGIN = 12;

/** Bounds over the pins, the trip and the tracks; the model's bbox when none of them has a coordinate. */
function focusBounds(model: MapModel): [number, number, number, number] | null {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  const add = (lon: number, lat: number): void => {
    minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  };
  for (const p of model.pins) add(p.lon, p.lat);
  for (const l of drawnRoute(model)) {
    if (l.alternative) continue;
    for (const n of l.nodes) if (n.lat !== undefined && n.lon !== undefined) add(n.lon, n.lat);
    for (const [lon, lat] of l.path) add(lon, lat);
  }
  for (const t of model.tracks ?? []) for (const [lon, lat] of t.path) add(lon, lat);
  return Number.isFinite(minLon) ? [minLon, minLat, maxLon, maxLat] : model.bbox;
}

const trackColour = (i: number): string => TRACK_PALETTE[i % TRACK_PALETTE.length]!;

/** "Heide-Shuttle Ring 1" → "Ring 1"; a name without such a token is cut to fit a badge. */
function trackLabel(t: MapTrack): string {
  const m = /\b(Ring|Linie|Tour|Route|Weg|Etappe)\s*\d+[a-z]?\b/i.exec(t.name);
  if (m) return m[0];
  return t.name.length > 20 ? t.name.slice(0, 19) + '…' : t.name;
}

/** Point at half the polyline's length. */
function midpoint(coords: [number, number][]): [number, number] {
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const d = Math.hypot(coords[i]![0] - coords[i - 1]![0], (coords[i]![1] - coords[i - 1]![1]) * 1.6);
    seg.push(d);
    total += d;
  }
  let acc = 0;
  for (let i = 0; i < seg.length; i++) {
    if (acc + seg[i]! >= total / 2) {
      const t = seg[i]! ? (total / 2 - acc) / seg[i]! : 0;
      const a = coords[i]!, b = coords[i + 1]!;
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    acc += seg[i]!;
  }
  return coords[Math.floor(coords.length / 2)]!;
}

const modeKey = (m: string): string => m.toLowerCase();
const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });
const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function popupHtml(p: MapPin): string {
  const parts: string[] = [];
  if (p.image) parts.push(`<img class="popup-img" src="${esc(p.image)}" alt="" loading="lazy">`);
  parts.push(`<strong class="popup-title">${esc(p.name)}</strong>`);
  if (p.address) parts.push(`<div class="popup-line">${esc(p.address)}</div>`);
  if (p.facts?.length) parts.push(`<dl class="popup-facts">${p.facts.map((f) => `<dt>${esc(f.label)}</dt><dd>${esc(f.value)}</dd>`).join('')}</dl>`);
  if (p.openingHours) parts.push(`<div class="popup-line">${esc(p.openingHours)}</div>`);
  if (p.price) parts.push(`<div class="popup-line">${esc(p.price)}</div>`);
  // what the answer itself said about this place — the popup never says less than the text
  if (p.quote) parts.push(`<div class="popup-quote">${renderMarkdown(p.quote)}</div>`);
  if (p.description) parts.push(`<div class="popup-desc">${esc(p.description.length > 220 ? p.description.slice(0, 217) + '…' : p.description)}</div>`);
  const foot: string[] = [];
  if (p.hubUrl) foot.push(`<a class="popup-link" href="${esc(p.hubUrl)}" target="${linkTarget(p.hubUrl)}" rel="noopener">Auf entdecke-nds.de ansehen</a>`);
  for (const l of p.links ?? []) if (l.url !== p.hubUrl) foot.push(`<a class="popup-link popup-link-more" href="${esc(l.url)}" target="_blank" rel="noopener">${esc(l.label)}</a>`);
  const src = sourceLabel(p.source);
  if (src) foot.push(`<span class="popup-source">${esc(src)}</span>`);
  if (foot.length) parts.push(`<div class="popup-foot">${foot.join('')}</div>`);
  return `<div class="popup">${parts.join('')}</div>`;
}

/** The widget lives on entdecke-nds.de itself: a link into the same site stays in the tab,
 *  everything else opens a new one so the conversation is not lost. */
function linkTarget(url: string): string {
  try {
    return new URL(url).hostname === location.hostname ? '_self' : '_blank';
  } catch {
    return '_blank';
  }
}

function sourceLabel(source: string | undefined): string | null {
  if (!source) return null;
  if (source === 'niedersachsen-hub') return 'Quelle: Niedersachsen-Tourismus-Hub';
  if (source === 'osm') return 'Quelle: OpenStreetMap';
  if (source === 'destination.one') return 'Quelle: destination.one Open Data';
  return null;
}

// pin colours come from the tokens in styles.css (.pin-poi, .pin-place, .pin-stop, .pin-shape, .pin-dot)
const ICON_DROP = '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path class="pin-shape" d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z" stroke-width="1.5"/><circle class="pin-dot" cx="12" cy="9" r="2.6"/></svg>';
const ICON_STOP = '<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><circle class="pin-shape" cx="12" cy="12" r="8" stroke-width="2.5"/><circle class="pin-dot" cx="12" cy="12" r="3"/></svg>';
